import React, { useEffect, useState } from "react";
import { Button } from "../../../../components/ui/button";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as hostService from "../../../../api/services/hostService";
import * as userService from "../../../../api/services/userServices";
import { apiClient, userApiClient } from '../../../../api/HttpClient';
import { useUserContext } from "../../../protectedRoute/UserContext";
import { Plus, Building2, DollarSign, CheckCircle2, XCircle, LogOut, Bell, Menu, X, BedDouble, Sparkles, CalendarCheck, TrendingUp, Percent, Home, MapPin, Phone, Mail, Upload, Utensils, ShieldCheck, AlertCircle, Settings as SettingsIcon, Store, CreditCard, Loader2, Zap, Users } from 'lucide-react';
import TeamManagementTab from '../../../../components/PropertyDashboard/TeamManagementTab';
import { useNotifications } from '../../../../context/NotificationContext';
import PgMarketplace from '../../../../components/PgMarketplace/PgMarketplace';
import PropertyCard from "./PropertyCard";
import Settings from "../DashboardComponents/Settings";
import SubscriptionPage from '../../../../components/SubscriptionPage/SubscriptionPage';
import { Avatar, AvatarFallback, AvatarImage } from "../../../../components/ui/avatar";
import { cn } from "../../../../lib/utils";
import { Dialog, DialogContent } from "../../../../components/ui/dialog";
import CreatePropertyPage from "./MyProperty/CreateProperty/CreateProperty";
import HostVerificationModal from "../../../../components/HostVerificationModal/HostVerificationModal";
import { Snackbar, Alert, CircularProgress } from "@mui/material";

interface Option {
  id: string;
  name: string;
  address: string;
  images: { image?: string }[]; // Updated to match IImages[]
  status: string;
  beds: number;
  is_verified: boolean;
}

interface UserProfile {
  first_name?: string;
  last_name?: string;
  email?: string;
}

const HomePage: React.FC = () => {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState<string>('Home');
  const [hostVerificationStatus, setHostVerificationStatus] = useState<string | null>(null); // New state for host verification status
  const [propertyVerificationStatuses, setPropertyVerificationStatuses] = useState<Record<string, string>>({}); // New state for property verification statuses
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreatePropertyDialog, setShowCreatePropertyDialog] = useState(false);
  const [showHostVerificationModal, setShowHostVerificationModal] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState<boolean | null>(null);
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [bankForm, setBankForm] = useState({ account_number: '', ifsc_code: '' });
  const [bankSaving, setBankSaving] = useState(false);
  const [bankError, setBankError] = useState('');

  const [notifOpen, setNotifOpen] = useState(false);
  const { notifications, unreadCount, clearAll, markRead } = useNotifications();

  const navigate = useNavigate();
  const { state, dispatch } = useUserContext();
  const { organization_id } = state;
  const location = useLocation();

  useEffect(() => {
    if (organization_id) {
      fetchHostPropertyOptions();
      fetchUserProfile();
      fetchHostVerificationStatus();
      fetchPropertyVerificationStatuses();
    }
  }, [organization_id]);

  // Only check bank details once verification is confirmed
  useEffect(() => {
    if (hostVerificationStatus === 'VERIFIED' && organization_id) {
      fetchBankDetails();
    }
  }, [hostVerificationStatus]);

  const fetchBankDetails = async () => {
    if (!organization_id) return;
    const exists = await userService.checkBankDetails(organization_id);
    setHasBankDetails(exists);
    if (!exists) setShowBankDetailsModal(true);
  };

  const handleSaveBankDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    const accountNumber = bankForm.account_number.trim().replace(/\s+/g, '');
    const ifscCode = bankForm.ifsc_code.trim().toUpperCase();

    if (!accountNumber || !ifscCode) {
      setBankError('Both fields are required.');
      return;
    }
    if (!/^\d{9,18}$/.test(accountNumber)) {
      setBankError('Enter a valid bank account number (9 to 18 digits, numbers only).');
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      setBankError('Enter a valid IFSC code (e.g. HDFC0001234).');
      return;
    }
    setBankError('');
    setBankSaving(true);
    try {
      await userService.addBankDetails({
        bank_account_number: accountNumber,
        ifsc_code: ifscCode,
      });
      setHasBankDetails(true);
      setShowBankDetailsModal(false);
      showSnackbar('Bank details saved successfully!', 'success');
    } catch (err: any) {
      const apiMsg =
        err?.message ||
        err?.data?.bank_account_number?.[0] ||
        err?.data?.ifsc_code?.[0] ||
        'Failed to save bank details. Please try again.';
      setBankError(apiMsg);
    } finally {
      setBankSaving(false);
    }
  };

  useEffect(() => {
    if (location.state?.propertyCreationSuccess) {
      showSnackbar("Property created successfully!", "success");
      fetchHostPropertyOptions();
      setActiveTab("My Properties");
      setShowCreatePropertyDialog(false);
      // Clear the state to prevent re-fetching on subsequent renders
      navigate(location.pathname, { replace: true });
    }

    if (location.state?.verificationPending) {
      showSnackbar("Verification submitted successfully. Please wait for admin approval.", "success");
      fetchHostVerificationStatus();
      // Clear the state to prevent re-fetching on subsequent renders
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  const fetchPropertyVerificationStatuses = async () => {
    try {
      const client = await apiClient;
      const response = await client.get<Array<{ property: number; status: string }>>(`${process.env.REACT_APP_USER_SERVICE_API_ENDPOINT}/api/hosts/property-verifications/my_verifications/`);
      const statuses: Record<number, string> = {};
      response.forEach(item => {
        statuses[item.property] = item.status;
      });
      setPropertyVerificationStatuses(statuses);
    } catch (error) {
      console.error("Failed to fetch property verification statuses:", error);
      setPropertyVerificationStatuses({}); // Reset or handle error state
    }
  };

  const fetchHostVerificationStatus = async () => {
    try {
      const client = await userApiClient;
      const response = await client.get<{ status: string }>('/api/users/organization-verifications/my_verification/');
      setHostVerificationStatus(response.status);
      dispatch({ type: 'SET_ORGANIZATION_VERIFIED', payload: response.status === 'VERIFIED' });
    } catch (error) {
      console.error("Failed to fetch host verification status:", error);
      setHostVerificationStatus(null); // Reset or handle error state
    }
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await userService.userProfileService();
      setUserProfile(profile);
      // Dispatch SET_USER to update the global UserContext state
      dispatch({ type: 'SET_USER', payload: {
        first_name: profile.first_name ?? '',
        last_name: profile.last_name ?? '',
        email: profile.email ?? '',
        phone_number: profile.phone_number ?? '',
      } });
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      showSnackbar("Failed to fetch user profile", "error");
    }
  };

  const fetchHostPropertyOptions = async () => {
    setLoading(true);
    try {
      const response = await hostService.getHostProprtyOptions(organization_id!);
      // Mock data for address, image, status, and beds
      const optionsWithMockData = response.map((option: any) => ({
        ...option,
        address: `${option.location.locality}, ${option.location.city}`,
        status: 'Active',
        beds: 5,
        images: option.images || [], // Ensure images array is passed, default to empty array
      }));
      setOptions(optionsWithMockData);

      // If no properties, ensure propertyID in context is null
      if (optionsWithMockData.length === 0) {
        dispatch({ type: 'SET_PROPERTY_ID', payload: null });
      }
    } catch (error) {
      console.error("Failed to fetch host property options:", error);
      showSnackbar("Failed to fetch properties", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCreatePropertyClick = () => {
    if (!state.organization_is_verified) {
      setShowHostVerificationModal(true);
    } else if (!hasBankDetails) {
      setShowBankDetailsModal(true);
    } else {
      setShowCreatePropertyDialog(true);
    }
  };

  const sidebarItems = [
    { label: "Home", icon: Home },
    { label: "My Properties", icon: Building2 },
    { label: "PG Marketplace", icon: Store },
    { label: "Subscription", icon: Zap },
    { label: "Earnings", icon: DollarSign },
    { label: "Team", icon: Users },
    { label: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-secondary/20 to-secondary/10 border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-2xl md:text-3xl font-bold italic text-foreground">Social Bunkr</span>
          </Link>

          

          <div className="flex items-center gap-2">
            {/* Notification bell */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setNotifOpen(o => !o); markRead(); }}
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>

              {notifOpen && (
                <div>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <span className="font-semibold text-sm text-gray-800">Notifications</span>
                      {notifications.length > 0 && (
                        <button className="text-xs text-[#276859] hover:underline" onClick={clearAll}>
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications yet</div>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50">
                            <p className="text-sm font-medium text-gray-800">{n.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{n.body}</p>
                            <p className="text-[10px] text-gray-400 mt-1">{n.time.toLocaleTimeString()}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={cn(
          "fixed md:sticky top-[65px] left-0 z-40 h-[calc(100vh-65px)] w-64 bg-card border-r border-border transition-transform duration-300 md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="p-6">
            {/* User Profile */}
            <div className="flex items-center gap-3 mb-8">
              <Avatar className="h-12 w-12 border-2 border-muted">
                <AvatarImage src="" />
                <AvatarFallback className="bg-muted text-muted-foreground">
                  {userProfile ? userProfile.first_name?.charAt(0) : ''}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-foreground">{userProfile ? `${userProfile.first_name} ${userProfile.last_name}`: ''}</h3>
                <p className="text-sm text-muted-foreground">{userProfile ? userProfile.email : ''}</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="space-y-2">
              {sidebarItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setActiveTab(item.label);
                    setSidebarOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors",
                    activeTab === item.label
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  dispatch({ type: 'RESET_USER' });
                  navigate('/login');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <LogOut className="h-5 w-5" />
                Logout
              </button>
            </nav>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8">
          {/* Home Tab */}
          {activeTab === "Home" && (
            <div className="h-full">
              {/* Host Verification Banner - Show if not verified */}
              {!state.organization_is_verified && (
                <div className="relative mb-4 p-5 rounded-xl bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500 shadow-lg shadow-amber-500/40 ring-2 ring-amber-300 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 overflow-hidden">
                  <span className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-amber-400/60 animate-ping opacity-40" />
                  <div className="relative flex items-start gap-3">
                    <div className="p-2 bg-white/30 backdrop-blur rounded-full">
                      <AlertCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Verify Your Host Account</h3>
                      <p className="text-sm text-white/90">Complete verification to start listing properties and earning.</p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="relative bg-white text-amber-700 hover:bg-amber-50 hover:text-amber-800 font-bold whitespace-nowrap shadow-xl shadow-black/20 hover:scale-105 transition-transform animate-pulse"
                    onClick={() => setShowHostVerificationModal(true)}
                  >
                    <ShieldCheck className="h-5 w-5 mr-2" />
                    Verify Now
                  </Button>
                </div>
              )}

              {/* Promotional Banner with Image - Full Height */}
              <div className={cn(
                "rounded-xl overflow-hidden border border-primary/20 bg-card",
                state.organization_is_verified ? "h-[calc(100vh-180px)]" : "h-[calc(100vh-280px)]"
              )}>
                <div className="relative h-full">
                  <img 
                    src="https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=1200&h=800&fit=crop"
                    alt="PG Beds"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute top-6 left-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-white">Welcome back, {userProfile ? userProfile.first_name : ''}! 👋</h1>
                  </div>
                  <div className="absolute top-6 right-6">
                    <Button
                      className={cn(
                        "text-primary-foreground bg-primary hover:bg-primary/90",
                        !state.organization_is_verified && "opacity-40 grayscale cursor-not-allowed pointer-events-none"
                      )}
                      disabled={!state.organization_is_verified}
                      onClick={handleCreatePropertyClick}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Property
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-5 w-5 text-secondary" />
                      <span className="text-secondary font-medium text-sm">Maximize Your Earnings</span>
                    </div>
                    <h3 className="text-2xl md:text-4xl font-bold text-white mb-3">
                      List Your Vacant PG Beds & Earn Extra! 💰
                    </h3>
                    <p className="text-white/80 text-base md:text-lg mb-6 max-w-2xl">
                      Got empty beds in your PG? Or tenants going on holiday? List their beds for short stays and maximize your earnings!
                    </p>
                    <Button
                      className={cn(
                        "font-semibold",
                        state.organization_is_verified
                          ? "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                          : "bg-muted text-muted-foreground cursor-not-allowed opacity-40 grayscale pointer-events-none"
                      )}
                      disabled={!state.organization_is_verified}
                    >
                      <BedDouble className="h-5 w-5 mr-2" />
                      List Vacant Beds
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* My Properties Tab */}
          {activeTab === "My Properties" && (
            <div>
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-primary">My Properties</h1>
              <Button 
                  className="text-primary-foreground bg-primary hover:bg-primary/90"
                  disabled={!state.organization_is_verified}
                  onClick={handleCreatePropertyClick}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Property
                </Button>
              </div>

              {/* Property List */}
              <div className="space-y-4">
                {loading ? (
                  <CircularProgress />
                ) : (
                  options.map((option) => (
                    <PropertyCard
                      key={option.id}
                      id={option.id}
                      name={option.name}
                      address={option.address}
                      images={option.images}
                      status={option.status}
                      beds={option.beds}
                      is_verified={option.is_verified}
                      propertyVerificationStatus={propertyVerificationStatuses[option.id] || 'NOT_SUBMITTED'} // Pass property verification status
                      onPropertyVerified={fetchHostPropertyOptions}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'Earnings' && (
            <div className="text-center py-16">
              <DollarSign className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Earnings Dashboard</h2>
              <p className="text-muted-foreground">Track your property earnings and payouts here.</p>
            </div>
          )}

          {activeTab === 'Team' && (
            <TeamManagementTab />
          )}

          {activeTab === 'Settings' && (
            <Settings onGoToSubscription={() => setActiveTab('Subscription')} />
          )}

          {activeTab === 'Subscription' && (
            <SubscriptionPage />
          )}

          {activeTab === 'PG Marketplace' && (
            <PgMarketplace />
          )}
        </main>
      </div>
      <Dialog open={showCreatePropertyDialog} onOpenChange={setShowCreatePropertyDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-card">
          <CreatePropertyPage showInDialog={true} onClose={() => setShowCreatePropertyDialog(false)} onPropertyCreated={fetchHostPropertyOptions} />
        </DialogContent>
      </Dialog>
      <HostVerificationModal
        open={showHostVerificationModal}
        onClose={() => setShowHostVerificationModal(false)}
        onVerificationSuccess={() => {
            fetchHostVerificationStatus();
            showSnackbar("Verification submitted successfully. Please wait for admin approval.", "success");
        }}
      />

      {/* Bank Details Setup Modal */}
      {showBankDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#276859] to-[#2A5248] px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg">Set Up Bank Details</h2>
                  <p className="text-white/70 text-sm">Required before you can create a property</p>
                </div>
              </div>
            </div>

            {/* Steps indicator */}
            <div className="flex items-center gap-0 px-6 pt-5 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#276859] flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs font-semibold text-[#276859]">Profile Verified</span>
              </div>
              <div className="flex-1 h-0.5 bg-[#276859] mx-2" />
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#FBBD23] flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-[#276859]" />
                </div>
                <span className="text-xs font-semibold text-[#FBBD23]">Bank Details</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200 mx-2" />
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-gray-400" />
                </div>
                <span className="text-xs font-semibold text-gray-400">Create Property</span>
              </div>
            </div>

            <form onSubmit={handleSaveBankDetails} className="px-6 pb-6 pt-4 space-y-4">
              <p className="text-sm text-gray-500">
                Your bank details are used to receive payouts when guests book your property. This is a one-time setup.
              </p>

              {bankError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {bankError}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-[#276859] mb-1">Bank Account Number *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter your bank account number"
                  value={bankForm.account_number}
                  onChange={e => setBankForm(f => ({ ...f, account_number: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]"
                  maxLength={18}
                  pattern="\d{9,18}"
                  title="Bank account number must be 9 to 18 digits."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#276859] mb-1">IFSC Code *</label>
                <input
                  type="text"
                  placeholder="e.g. SBIN0001234"
                  value={bankForm.ifsc_code}
                  onChange={e => setBankForm(f => ({ ...f, ifsc_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859] uppercase"
                  maxLength={11}
                  pattern="[A-Z]{4}0[A-Z0-9]{6}"
                  title="IFSC must be 11 characters: 4 letters, then 0, then 6 letters or digits."
                  required
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                🔒 Your bank details are securely stored and only used for payout processing.
              </div>

              <Button
                type="submit"
                disabled={bankSaving}
                className="w-full bg-[#276859] hover:bg-[#1f5246] text-white font-bold py-3 rounded-xl"
              >
                {bankSaving ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</>
                ) : (
                  'Save & Continue to Dashboard'
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default HomePage;